import { Badge, Box, Button, Menu, Switch, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { DataTableColumn } from 'mantine-datatable';
import { useCallback, useEffect, useState } from 'react';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useCompanyDocuments } from '../../hooks/useCompanyDocuments';
import { useCreateCompanyDocument } from '../../hooks/useCreateCompanyDocument';
import { useDeleteCompanyDocument } from '../../hooks/useDeleteCompanyDocument';
import { useUpdateCompanyDocument } from '../../hooks/useUpdateCompanyDocument';
import { useOneDriveCompanyDocumentUpload } from '../../hooks/useOneDriveCompanyDocumentUpload';
import { CompanyDocument } from '../../types';
import AppendicesEmptyState from './AppendicesEmptyState';
import AppendixRowActions from './AppendixRowActions';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import ArchiveAppendixModal from './ArchiveAppendixModal';
import { useSharepoint } from 'src/data-capture/file-management/drop-zone/useSharepoint';
import { useSharePointPicker } from 'src/data-capture/file-management/drop-zone/SharePointProvider';
import { PickedItem } from 'src/data-capture/file-management/drop-zone/SharePointButton';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AppendicesListPage() {
  const { documents, isLoading: isLoadingDocuments } = useCompanyDocuments();
  const { createCompanyDocument, isLoading: isUploading } = useCreateCompanyDocument();
  const { deleteCompanyDocument, isLoading: isDeleting } = useDeleteCompanyDocument();
  const { updateCompanyDocument, isLoading: isUpdating } = useUpdateCompanyDocument();
  const { createLink } = useFileTemporaryLinkMutation();
  const { uploadOneDriveFiles, isUploading: isUploadingFromOneDrive } =
    useOneDriveCompanyDocumentUpload();
  const { pendingPickedItems, clearPendingPickedItems } = useSharePointPicker();

  const [showArchived, setShowArchived] = useState(false);
  const [documentToArchive, setDocumentToArchive] = useState<CompanyDocument | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<CompanyDocument | null>(null);

  const handleFileDrop = async (files: File[]) => {
    for (const file of files) {
      try {
        await createCompanyDocument(file);
      } catch (err) {
        notifyAndLogError(
          'Upload failed',
          `We were not able to upload ${file.name}. Please try again or contact support.`,
          err,
        );
      }
    }
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      createCompanyDocument(files[0]).catch(err => {
        notifyAndLogError(
          'Upload failed',
          'We were not able to upload the document. Please try again or contact support.',
          err,
        );
      });
    }
    event.target.value = '';
  };

  const handleOneDriveUpload = async (items: PickedItem[]) => {
    try {
      await uploadOneDriveFiles(items);
    } catch (err) {
      notifyAndLogError(
        'Upload failed',
        'We were not able to upload from SharePoint. Please try again or contact support.',
        err,
      );
    }
  };

  useEffect(() => {
    if (pendingPickedItems && pendingPickedItems.length > 0) {
      handleOneDriveUpload(pendingPickedItems);
      clearPendingPickedItems();
    }
  }, [pendingPickedItems, clearPendingPickedItems]);

  const { handleClick: handleSharePointClick } = useSharepoint({
    onPickedItems: handleOneDriveUpload,
    onAccountChange: () => {},
    switchAccountRequested: false,
    onSwitchAccountHandled: () => {},
    fileExtensions: ['.pdf', '.docx'],
  });

  const handleViewDocument = async (doc: CompanyDocument) => {
    try {
      const link = await createLink(doc.file.id);
      window.open(link.url, '_blank');
    } catch (err) {
      notifyAndLogError(
        'Error',
        'We were not able to open the document. Please try again or contact support.',
        err,
      );
    }
  };

  const handleArchiveConfirm = async () => {
    if (!documentToArchive) return;
    try {
      await updateCompanyDocument(documentToArchive.id, { isArchived: true });
      setDocumentToArchive(null);
    } catch (err) {
      notifyAndLogError(
        'Archive failed',
        'We were not able to archive the document. Please try again or contact support.',
        err,
      );
    }
  };

  const handleUnarchive = async (doc: CompanyDocument) => {
    try {
      await updateCompanyDocument(doc.id, { isArchived: false });
    } catch (err) {
      notifyAndLogError(
        'Unarchive failed',
        'We were not able to unarchive the document. Please try again or contact support.',
        err,
      );
    }
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    try {
      await deleteCompanyDocument(documentToDelete.id);
      setDocumentToDelete(null);
    } catch (err) {
      notifyAndLogError(
        'Delete failed',
        'We were not able to delete the document. Please try again or contact support.',
        err,
      );
    }
  };

  const filterPredicate = useCallback(
    (item: CompanyDocument, search: string): boolean => {
      const matchesSearch = item.file.fileName.toLowerCase().includes(search.toLowerCase());
      const matchesArchiveFilter = showArchived || !item.isArchived;
      return matchesSearch && matchesArchiveFilter;
    },
    [showArchived],
  );

  const availableDocuments = documents.filter(doc => !doc.isArchived);
  const allDocumentsArchived = documents.length > 0 && availableDocuments.length === 0;

  const columns: DataTableColumn<CompanyDocument>[] = [
    ...(showArchived
      ? [
          {
            accessor: 'isArchived',
            title: 'Status',
            width: 100,
            render: (record: CompanyDocument) =>
              record.isArchived ? (
                <Badge size="xs" color="gray">
                  Archived
                </Badge>
              ) : null,
          },
        ]
      : []),
    {
      accessor: 'file.fileName',
      title: 'Name',
      render: record => (
        <Box>
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
            {record.file.fileName}
          </Text>
          <Text size="xs" c="gray.6">
            {formatFileSize(record.file.fileSize)}
          </Text>
        </Box>
      ),
    },
    {
      accessor: 'uploadedByName',
      title: 'Uploaded by',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.uploadedByName ?? '-'}
        </Text>
      ),
    },
    {
      accessor: 'createdAt',
      title: 'Uploaded',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {dayjs(record.createdAt).format('D MMM YYYY')}
        </Text>
      ),
    },
    {
      accessor: 'actions',
      title: '',
      render: record => (
        <AppendixRowActions
          document={record}
          onView={handleViewDocument}
          onArchive={setDocumentToArchive}
          onUnarchive={handleUnarchive}
          onDelete={setDocumentToDelete}
          isUpdating={isUpdating}
        />
      ),
    },
  ];

  const isUploadingAny = isUploading || isUploadingFromOneDrive;

  const HeaderRow = (
    <>
      <Switch
        label="Show archived"
        checked={showArchived}
        onChange={event => setShowArchived(event.currentTarget.checked)}
        size="sm"
      />
      <Menu shadow="md">
        <Menu.Target>
          <Button variant="primary" loading={isUploadingAny}>
            Upload document
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item component="label" closeMenuOnClick={false} style={{ cursor: 'pointer' }}>
            Upload from your computer
            <input
              type="file"
              accept=".pdf,.docx"
              style={{ display: 'none' }}
              onChange={handleUpload}
            />
          </Menu.Item>
          <Menu.Item onClick={handleSharePointClick}>Upload from SharePoint</Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </>
  );

  if (!isLoadingDocuments && documents.length === 0) {
    return (
      <AppendicesEmptyState
        variant="no-documents"
        onFileDrop={handleFileDrop}
        onUpload={handleUpload}
        onSharePointClick={handleSharePointClick}
        isUploading={isUploading || isUploadingFromOneDrive}
      />
    );
  }

  if (!isLoadingDocuments && allDocumentsArchived && !showArchived) {
    return (
      <AppendicesEmptyState
        variant="all-archived"
        onFileDrop={handleFileDrop}
        onUpload={handleUpload}
        onSharePointClick={handleSharePointClick}
        onShowArchived={() => setShowArchived(true)}
        isUploading={isUploading || isUploadingFromOneDrive}
      />
    );
  }

  return (
    <>
      <SimpleTable<CompanyDocument>
        resource="company-documents"
        columns={columns}
        title="Appendices"
        HeaderRow={HeaderRow}
        enableSearch
        filterPredicate={filterPredicate}
        paginationDependencies={showArchived}
        pageSize={10}
        mode="client"
      />

      <ArchiveAppendixModal
        opened={!!documentToArchive}
        onClose={() => setDocumentToArchive(null)}
        document={documentToArchive}
        onConfirm={handleArchiveConfirm}
        isLoading={isUpdating}
      />

      <ConfirmDeleteModal
        opened={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete appendix"
        message={`Are you sure you want to delete ${documentToDelete?.file.fileName}? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </>
  );
}
